import { ADMIN_HOST } from '@padla/core/admin-access/admin-origin';
import { isStudioShareStartParam } from '@padla/core/studios/share-kinds';
import * as Sentry from '@sentry/react';
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  BrowserRouter,
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import config from '../config';
import { usePricing } from '../hooks/use-balance';
import { ADMIN_SESSION, authHeaders, type PanelAuth, PanelAuthContext } from '../lib/panel-auth';
import { type AdminAccountView, logoutHere } from './admin-access/admin-passkey';
import { type AuthOutcome, classifyAuthOutcome } from './auth-recovery';
import {
  fetchTutorialCompleted,
  postTutorialComplete,
  resolveTutorialVisibility,
} from './client-panel-state';
import { clientTimezone, reportTimezone } from './client-timezone';
import DirectiveStatusHost from './components/DirectiveStatusHost';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import { MeetPadlaWall } from './components/MeetPadlaWall';
// ── Eager — обёртки дерева / мелочь на критическом пути ──────────────────────
import Tutorial from './components/Tutorial.jsx';
import { Button, Spinner } from './components/ui.jsx';
import { sseBus } from './hooks/sse-bus';
import { type NoDialogEntry, parseNoDialogResponse } from './meet-padla';
import { legacyAstrologyPathToAstro } from './pages/Astro/astro-routes';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from './stored-token';
import { usePanelTelemetry } from './telemetry/panel-telemetry';

// ── Lazy: route-level code-splitting (П20 bundle<500KB / П21 industry-standard) ─
// Каждый top-level экран — отдельный чанк, грузится ТОЛЬКО когда юзер на него
// зашёл. Корень P1 («Студия грузится мучительно долго даже на флагмане»): до
// этого ВСЁ — весь админ-флот (NerveCenter→three.js, Economics→recharts/chart.js,
// GraphV3, Viewer) + ВСЕ студии + стенды — лежало в одном монолите ~1.18MB gzip,
// который качал даже Telegram-юзер, открывший /astrology-studio с «тетриса».
// Теперь /astrology-studio тянет только чанк студии + общий вендор, без админки.
// Замер до/после — см. handoff 2026-06-13-studios-perf-responsive-deploy §2.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Viewer = lazy(() => import('./pages/Viewer/index.jsx'));
const Prompts = lazy(() => import('./pages/Prompts'));
const Gather = lazy(() => import('./pages/Gather'));
const Economics = lazy(() => import('./pages/Economics'));
const Billing = lazy(() => import('./pages/Billing'));
const UserBalance = lazy(() => import('./pages/UserBalance'));
const PaymentReturn = lazy(() => import('./pages/PaymentReturn'));
const GiftToChat = lazy(() => import('./pages/GiftToChat'));
const Referrals = lazy(() => import('./pages/Referrals'));
const Logs = lazy(() => import('./pages/Logs'));
const Observatory = lazy(() => import('./pages/Observatory'));
const GraphV3 = lazy(() => import('./pages/GraphV3'));
// Вход в админку ключом доступа и привязка первого ключа по ссылке от бота (26 сен 2026).
const AdminLogin = lazy(() => import('./admin-access/AdminLogin'));
const AdminEnroll = lazy(() => import('./admin-access/AdminEnroll'));
const Security = lazy(() => import('./pages/Security'));
const DevLog = lazy(() => import('./pages/DevLog'));
const Devices = lazy(() => import('./pages/Devices'));
const BodyLabeling = lazy(() => import('./pages/BodyLabeling'));
const UserPersona = lazy(() => import('./pages/UserPersona'));
const StyleSwitcher = lazy(() => import('./pages/StyleSwitcher'));
const GiftRedeem = lazy(() => import('./pages/GiftRedeem'));
const MyGroups = lazy(() => import('./pages/MyGroups'));
const MyGroupDetail = lazy(() =>
  import('./pages/MyGroups').then((m) => ({ default: m.MyGroupDetail }))
);
const ProductPlacements = lazy(() => import('./pages/ProductPlacements'));
const NerveCenter = lazy(() => import('./pages/NerveCenter'));
const ChatPrimitivesDemo = lazy(() => import('./pages/Dev/ChatPrimitivesDemo'));
// named-экспорты — lazy требует модуль с default, поэтому маппим { X } → { default: X }:
const StudiosAdmin = lazy(() =>
  import('./pages/StudiosAdmin').then((m) => ({ default: m.StudiosAdmin }))
);
const TrackStudioShell = lazy(() =>
  import('./pages/TrackStudio/TrackStudioShell').then((m) => ({ default: m.TrackStudioShell }))
);
const TestsStudioShell = lazy(() =>
  import('./pages/TestsStudio/TestsStudioShell').then((m) => ({ default: m.TestsStudioShell }))
);
// Витрина астрослужбы — standalone AstroShell на /astro (подмена входа 6 авг;
// спека 2026-08-06-svaha-rebuild-design.md). Старый адрес /astrology-studio
// редиректится маппером legacyAstrologyPathToAstro — отправленные ссылки живут.
const AstroShell = lazy(() =>
  import('./pages/Astro/AstroShell').then((m) => ({ default: m.AstroShell }))
);
const AnimTestHarness = lazy(() =>
  import('./pages/StudiosShared/AnimTestHarness').then((m) => ({ default: m.AnimTestHarness }))
);
const ScreensStand = lazy(() =>
  import('./pages/StudiosShared/ScreensStand').then((m) => ({ default: m.ScreensStand }))
);
const PrefillPreviewStand = lazy(() =>
  import('./pages/StudiosShared/PrefillPreviewStand').then((m) => ({
    default: m.PrefillPreviewStand,
  }))
);
const StudiosSandboxViewer = lazy(() =>
  import('./pages/StudiosShared/StudiosSandboxViewer').then((m) => ({
    default: m.StudiosSandboxViewer,
  }))
);

// ViewerShell — full-bleed wrapper для /viewer/*.
//
// КРИТИЧЕСКИЙ БАГ (apr 2026, инцидент с Бахаревым/Юлей):
//   raw `\d+` НЕ матчит отрицательные chat_id (groups: `-...`,
//   supergroups: `-100...`). URL `/viewer/chat/-1003747596893` шёл
//   в container path → main centred, по бокам чёрные полосы на 32"+.
//   Личные чаты (положительный id) шли в full-width. Ректор видел
//   асимметрию «личка широкая, группа узкая» именно по этому regex.
//   Fix: `-?\d+` принимает оба знака.
//
// Operator console (ChatList /viewer index) тоже full-bleed — там
// своя 1480 max-width и hero header.
function ViewerShell({ children }: { children: React.ReactNode }) {
  return <div className="w-full">{children}</div>;
}

// ── Types ────────────────────────────────────────────────────────────────
/**
 * `passkey` — живая серверная сессия админа по ключу доступа (кука HttpOnly, пропуска у страницы
 * нет); `telegram` — мини-апп, всегда пользователь (в мини-аппе админки нет — ленд З).
 */
type AuthMethod = 'telegram' | 'passkey' | null;
type Role = 'user' | 'admin' | 'superadmin' | string;

interface AuthUser {
  id?: number | string;
  tg_id?: number | string;
  tgId?: number | string;
  tg_username?: string;
  email?: string;
  displayName?: string;
  role?: Role;
  [key: string]: unknown;
}

interface AuthState {
  loading: boolean;
  token: string | null;
  role: Role;
  user: AuthUser | null;
  error: string | null;
  authMethod: AuthMethod;
  /** Заполнено, когда сервер ответил «сначала чат с Падлой» (08.08.2026): дверь в переписку. */
  meet?: NoDialogEntry | null;
  /** Почему снова экран входа: живая сессия ключа закрылась (вышли на другом устройстве, «не я», срок). */
  notice?: 'session_ended' | null;
}

interface UseAuthReturn extends AuthState {
  handlePasskeyLogin: (account: AdminAccountView) => void;
  handleLogout: () => Promise<void>;
}

/** Чем входит панель: живая сессия ключа или пропуск (мини-апп, старый вход по паролю). */
function panelAuthOf(state: AuthState): PanelAuth | null {
  return state.authMethod === 'passkey' ? ADMIN_SESSION : state.token;
}

/** Состояние «вошёл ключом»: пропуска нет, роль — из учётки (шлюз сверяет её на каждом запросе). */
function passkeyState(account: AdminAccountView): AuthState {
  return {
    loading: false,
    token: null,
    role: account.role,
    user: { displayName: account.displayName, role: account.role },
    error: null,
    authMethod: 'passkey',
  };
}

const LOGGED_OUT: AuthState = {
  loading: false,
  token: null,
  role: 'user',
  user: null,
  error: null,
  authMethod: null,
};

const apiBase = (import.meta.env.VITE_API_BASE || '/api').replace(/\/$/, '');

/**
 * Пропуска старого входа по паролю в хранилище браузера: админских прав они больше не дают
 * (ленд З спеки 2026-09-26), панель их не читает — только стирает, чтобы не лежали.
 */
function forgetOldWebLogin(): void {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    /* хранилище недоступно — забывать нечего */
  }
}

// Check if running inside Telegram.
// Telegram types are ambient globals provided by the embedded webapp script.
function isTelegramWebApp(): boolean {
  // @ts-expect-error TODO migration: add proper Telegram WebApp type declarations
  return !!window?.Telegram?.WebApp?.initData;
}

function useAuth(): UseAuthReturn {
  const [state, setState] = useState<AuthState>({
    loading: true,
    token: null,
    role: 'user',
    user: null,
    error: null,
    authMethod: null,
  });

  const handlePasskeyLogin = useCallback((account: AdminAccountView) => {
    setState(passkeyState(account));
  }, []);

  // Выход на этом устройстве — из всех способов сразу: живой сессии ключа (шлюз гасит её в базе и
  // стирает куку) и старого входа по паролю; иначе перезагрузка молча вернула бы оставшимся.
  const handleLogout = useCallback(async (): Promise<void> => {
    if (window.location.hostname === ADMIN_HOST) {
      try {
        await logoutHere(apiBase);
      } catch {
        // Сеть легла — кука HttpOnly гаснет по сроку; локальная уборка ниже всё равно идёт.
      }
    }
    forgetOldWebLogin();
    setState({
      loading: false,
      token: null,
      role: 'user',
      user: null,
      error: null,
      authMethod: null,
    });
  }, []);

  // Бутстрап авторизации. Корень бага: транзиентный 503 (NPM HTML)/сеть/не-JSON
  // трактовался как «токен невалиден» → логаут + СТИРАНИЕ кред, и юзер не мог войти
  // обратно. Теперь ветвимся по КЛАССУ исхода (auth-recovery.ts): креды чистим ТОЛЬКО
  // при подтверждённом отказе (401/403/404, либо refresh тоже отверг); на серверной/
  // сетевой беде держим креды и ретраим с backoff (loading=true → спиннер, не логин).
  useEffect(() => {
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const BACKOFFS_MS = [500, 1000, 2000, 4000];

    async function attemptAuth(
      url: string,
      init?: RequestInit
    ): Promise<{
      outcome: AuthOutcome;
      data: { ok?: boolean; [k: string]: unknown } | null;
      status: number | null;
    }> {
      let resp: Response;
      try {
        resp = await fetch(url, init);
      } catch {
        return { outcome: 'unavailable', data: null, status: null }; // сеть упала
      }
      let data: { ok?: boolean; [k: string]: unknown } | null = null;
      try {
        data = (await resp.json()) as { ok?: boolean; [k: string]: unknown };
      } catch {
        // Тело не JSON (типичный 503-HTML от прокси): 2xx считаем серверной аномалией.
        return {
          outcome: classifyAuthOutcome({ status: resp.ok ? 500 : resp.status }),
          data: null,
          status: resp.status,
        };
      }
      return {
        outcome: classifyAuthOutcome({ status: resp.status, bodyOk: data?.ok }),
        data,
        status: resp.status,
      };
    }

    function showLogin(): void {
      if (cancelled) return;
      setState({
        loading: false,
        token: null,
        role: 'user',
        user: null,
        error: isTelegramWebApp() ? 'Telegram auth failed' : null,
        authMethod: null,
      });
    }

    function scheduleRetry(attempt: number): void {
      if (cancelled) return;
      if (attempt >= BACKOFFS_MS.length) {
        // Быстрые ретраи исчерпаны. Креды НЕ трогаем — reload авто-восстановит сессию,
        // когда сервер очнётся; пока показываем вход (web) / ошибку (Telegram).
        showLogin();
        return;
      }
      setState((prev) => ({ ...prev, loading: true, error: null }));
      timers.push(
        setTimeout(() => {
          void bootstrap(attempt + 1);
        }, BACKOFFS_MS[attempt])
      );
    }

    async function bootstrap(attempt = 0): Promise<void> {
      if (cancelled) return;

      // 1. Telegram initData → JWT.
      if (isTelegramWebApp()) {
        // @ts-expect-error TODO migration: Telegram WebApp types
        const initData = window.Telegram.WebApp.initData;
        const tg = await attemptAuth(`${apiBase}/v1/telegram/auth`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ initData, clientTimezone: clientTimezone() }),
        });
        if (cancelled) return;
        if (tg.outcome === 'ok' && tg.data) {
          setState({
            loading: false,
            token: tg.data.token as string,
            role: tg.data.role as Role,
            user: tg.data.user as AuthUser,
            error: null,
            authMethod: 'telegram',
          });
          return;
        }
        // «Сначала чат с Падлой» (08.08.2026): не отказ и не сбой — человек просто ещё не
        // знаком. Web-путь ему не поможет, поэтому уходим на экран знакомства, а не в логин.
        const meet = parseNoDialogResponse(tg.status ?? 0, tg.data);
        if (meet) {
          setState({
            loading: false,
            token: null,
            role: 'user',
            user: null,
            error: null,
            authMethod: null,
            meet,
          });
          return;
        }
        if (tg.outcome === 'unavailable') {
          scheduleRetry(attempt);
          return;
        }
        // tg.outcome === 'unauthorized' → initData отвергнут; пробуем web-путь ниже.
      }

      // 2. Живая сессия админа по ключу доступа — только на адресе админки (кука привязана к нему).
      //    Сервер решает сам по куке на каждом запросе; здесь лишь узнаём, есть ли она.
      if (window.location.hostname === ADMIN_HOST) {
        const me = await attemptAuth(`${apiBase}/v1/admin/me`);
        if (cancelled) return;
        if (me.outcome === 'ok' && me.data) {
          const a = (me.data.account ?? {}) as Partial<AdminAccountView>;
          setState(
            passkeyState({
              displayName: String(a.displayName ?? ''),
              role: a.role === 'superadmin' ? 'superadmin' : 'admin',
            })
          );
          return;
        }
        if (me.outcome === 'unavailable') {
          scheduleRetry(attempt);
          return;
        }
        // unauthorized — живой сессии нет.
      }

      // 3. Нет сессии — публичная страница. Пропуска старого входа по паролю не читаются вовсе.
      forgetOldWebLogin();
      showLogin();
    }

    void bootstrap(0);
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, []);

  // Репорт пояса устройства на каждом открытии (per-open захват, не только свежий логин).
  useEffect(() => {
    reportTimezone(apiBase, state.token);
  }, [state.token]);

  // Сторож живой сессии ключа: шлюз и так отказывает мёртвой сессии на каждом запросе, но экран
  // обязан это увидеть — «выйти везде» с другого устройства, кнопка «не я» или срок. Проверка при
  // возврате во вкладку и раз в минуту, пока вкладку видно.
  useEffect(() => {
    if (state.authMethod !== 'passkey') return;
    let stopped = false;
    const check = async (): Promise<void> => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      let status: number | null = null;
      try {
        status = (await fetch(`${apiBase}/v1/admin/me`)).status;
      } catch {
        return; // сеть легла — не приговор сессии
      }
      if (!stopped && status === 401) setState({ ...LOGGED_OUT, notice: 'session_ended' });
    };
    const onEvent = (): void => {
      void check();
    };
    const timer = setInterval(onEvent, 60_000);
    document.addEventListener('visibilitychange', onEvent);
    window.addEventListener('focus', onEvent);
    window.addEventListener('online', onEvent);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onEvent);
      window.removeEventListener('focus', onEvent);
      window.removeEventListener('online', onEvent);
    };
  }, [state.authMethod]);

  return { ...state, handlePasskeyLogin, handleLogout };
}

// ── AdminPanel ───────────────────────────────────────────────────────────
interface AdminPanelProps {
  /** Чем входит админка: живая сессия ключа или (до ленда З) старый пропуск веб-входа. */
  auth: PanelAuth;
  user: AuthUser | null;
  authMethod: AuthMethod;
  onLogout?: () => void;
  onPreviewAsUser?: () => void;
}

function AdminPanel({ auth, user, authMethod, onLogout, onPreviewAsUser }: AdminPanelProps) {
  // Сессия ключа — пустые заголовки (куку несёт браузер), пропуск — `Authorization`.
  const headers = useMemo(() => authHeaders(auth), [auth]);
  return (
    <PanelAuthContext.Provider value={auth}>
      <div className="min-h-full grid grid-rows-[auto,1fr] w-full overflow-x-hidden">
        {/* Чипы хода директивы создателя — один хост на всю админку (portal в body). */}
        <DirectiveStatusHost />
        <nav className="sticky top-0 z-10 border-b border-zinc-800 bg-zinc-900/60 backdrop-blur">
          <div className="container h-auto min-h-12 flex flex-wrap items-center gap-2">
            <div className="font-bold mr-1">PADLA</div>
            <NavLink
              className={({ isActive }) =>
                `px-2 py-1 rounded ${isActive ? 'bg-sky-900/80 text-sky-400' : 'hover:bg-zinc-800/60'}`
              }
              to="/"
            >
              Nerve Center
            </NavLink>
            <NavLink
              className={({ isActive }) =>
                `px-2 py-1 rounded ${isActive ? 'bg-zinc-800' : 'hover:bg-zinc-800/60'}`
              }
              to="/logs"
            >
              Logs
            </NavLink>
            <NavLink
              className={({ isActive }) =>
                `px-2 py-1 rounded ${isActive ? 'bg-zinc-800' : 'hover:bg-zinc-800/60'}`
              }
              to="/viewer"
            >
              Chats
            </NavLink>
            <NavLink
              className={({ isActive }) =>
                `px-2 py-1 rounded ${isActive ? 'bg-amber-900/40 text-amber-300' : 'hover:bg-zinc-800/60'}`
              }
              to="/studios-admin"
            >
              🌒 Studios
            </NavLink>
            <NavLink
              className={({ isActive }) =>
                `px-2 py-1 rounded ${isActive ? 'bg-zinc-800' : 'hover:bg-zinc-800/60'}`
              }
              to="/billing"
            >
              Billing
            </NavLink>
            <NavLink
              className={({ isActive }) =>
                `px-2 py-1 rounded ${isActive ? 'bg-rose-900/80 text-rose-400' : 'hover:bg-zinc-800/60'}`
              }
              to="/economics"
            >
              💰 P&L
            </NavLink>
            <NavLink
              className={({ isActive }) =>
                `px-2 py-1 rounded ${isActive ? 'bg-emerald-900/80 text-emerald-400' : 'hover:bg-zinc-800/60'}`
              }
              to="/referrals"
            >
              Refs
            </NavLink>
            <NavLink
              className={({ isActive }) =>
                `px-2 py-1 rounded ${isActive ? 'bg-zinc-800' : 'hover:bg-zinc-800/60'}`
              }
              to="/prompts"
            >
              Prompts
            </NavLink>
            <NavLink
              className={({ isActive }) =>
                `px-2 py-1 rounded ${isActive ? 'bg-teal-900/80 text-teal-300' : 'hover:bg-zinc-800/60'}`
              }
              to="/gather"
            >
              🎯 Добор
            </NavLink>
            <NavLink
              className={({ isActive }) =>
                `px-2 py-1 rounded ${isActive ? 'bg-purple-900/80 text-purple-400' : 'hover:bg-zinc-800/60'}`
              }
              to="/devices"
            >
              📱 Устройства
            </NavLink>
            <NavLink
              className={({ isActive }) =>
                `px-2 py-1 rounded ${isActive ? 'bg-purple-900/80 text-purple-400' : 'hover:bg-zinc-800/60'}`
              }
              to="/body"
            >
              🫀 Что это было
            </NavLink>
            <NavLink
              className={({ isActive }) =>
                `px-2 py-1 rounded ${isActive ? 'bg-purple-900/80 text-purple-400' : 'hover:bg-zinc-800/60'}`
              }
              to="/devlog"
            >
              📢 DevLog
            </NavLink>
            <NavLink
              className={({ isActive }) =>
                `px-2 py-1 rounded ${isActive ? 'bg-amber-900/80 text-amber-400' : 'hover:bg-zinc-800/60'}`
              }
              to="/security"
            >
              Security
            </NavLink>
            <NavLink
              className={({ isActive }) =>
                `px-2 py-1 rounded ${isActive ? 'bg-cyan-900/80 text-cyan-400' : 'hover:bg-zinc-800/60'}`
              }
              to="/graph-v3"
            >
              🔗 Graf V3
            </NavLink>
            <NavLink
              className={({ isActive }) =>
                `px-2 py-1 rounded ${isActive ? 'bg-pink-900/80 text-pink-400' : 'hover:bg-zinc-800/60'}`
              }
              to="/pp"
            >
              🏷 PP
            </NavLink>
            {/* User info and controls */}
            <div className="ml-auto flex items-center gap-2">
              {onPreviewAsUser && (
                <button
                  onClick={onPreviewAsUser}
                  className="px-2 py-1 rounded text-xs bg-sky-900/60 hover:bg-sky-800/80 text-sky-400 transition-colors"
                  title="Preview as user"
                >
                  User view
                </button>
              )}
              {user && (
                <span className="text-xs text-zinc-500">
                  {user.displayName || user.email || user.tg_username || `ID: ${user.tg_id}`}
                </span>
              )}
              {authMethod === 'passkey' && onLogout && (
                <button
                  onClick={onLogout}
                  className="px-2 py-1 rounded text-xs bg-zinc-800 hover:bg-red-900/60 hover:text-red-400 transition-colors"
                  title="Выйти"
                >
                  ⏻
                </button>
              )}
            </div>
          </div>
        </nav>
        <main className="w-full max-w-full overflow-x-hidden">
          <Suspense
            fallback={
              <div className="container py-3 sm:py-4">
                <Spinner />
              </div>
            }
          >
            <Routes>
              {/* NC gets full width — no container wrapper */}
              <Route index element={<NerveCenter headers={headers} apiBase={apiBase} />} />

              {/* All other routes keep the container */}
              <Route
                path="/dashboard"
                element={
                  <div className="container py-3 sm:py-4">
                    <Dashboard headers={headers} apiBase={apiBase} />
                  </div>
                }
              />
              <Route
                path="/observatory"
                element={
                  <div className="container py-3 sm:py-4">
                    <Observatory headers={headers} apiBase={apiBase} />
                  </div>
                }
              />
              <Route
                path="/logs"
                element={
                  <div className="container py-3 sm:py-4">
                    <Logs apiBase={apiBase} />
                  </div>
                }
              />
              <Route path="/chats" element={<Navigate to="/viewer" replace />} />
              <Route path="/chats/:chatId" element={<Navigate to="/viewer" replace />} />
              <Route
                path="/viewer/*"
                element={
                  <ViewerShell>
                    <Viewer headers={headers} apiBase={apiBase} />
                  </ViewerShell>
                }
              />
              <Route
                path="/prompts"
                element={
                  <div className="container py-3 sm:py-4">
                    <Prompts headers={headers} apiBase={apiBase} />
                  </div>
                }
              />
              <Route
                path="/gather"
                element={
                  <div className="container py-3 sm:py-4">
                    <Gather headers={headers} apiBase={apiBase} />
                  </div>
                }
              />
              <Route
                path="/studios-admin"
                element={<StudiosAdmin headers={headers} apiBase={apiBase} />}
              />
              <Route
                path="/billing"
                element={
                  <div className="container py-3 sm:py-4">
                    <Billing apiBase={apiBase} />
                  </div>
                }
              />
              <Route
                path="/economics"
                element={
                  <div className="container py-3 sm:py-4">
                    <Economics headers={headers} apiBase={apiBase} />
                  </div>
                }
              />
              <Route
                path="/referrals"
                element={
                  <div className="container py-3 sm:py-4">
                    <Referrals headers={headers} apiBase={apiBase} />
                  </div>
                }
              />
              <Route path="/devices" element={<Devices headers={headers} apiBase={apiBase} />} />
              <Route path="/body" element={<BodyLabeling headers={headers} apiBase={apiBase} />} />
              <Route
                path="/devlog"
                element={
                  <div className="container py-3 sm:py-4">
                    <DevLog headers={headers} apiBase={apiBase} />
                  </div>
                }
              />
              <Route
                path="/security"
                element={
                  <div className="container py-3 sm:py-4">
                    <Security headers={headers} apiBase={apiBase} />
                  </div>
                }
              />
              <Route
                path="/graph-v3"
                element={
                  <div className="container py-3 sm:py-4">
                    <GraphV3 headers={headers} apiBase={apiBase} />
                  </div>
                }
              />
              <Route
                path="/pp"
                element={
                  <div className="container py-3 sm:py-4">
                    <ProductPlacements headers={headers} apiBase={apiBase} />
                  </div>
                }
              />
              <Route path="/dev/chat-primitives" element={<ChatPrimitivesDemo />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </PanelAuthContext.Provider>
  );
}

// ── UserPanel ────────────────────────────────────────────────────────────
interface UserPanelProps {
  /** Пропуск мини-аппа — строка; «посмотреть как пользователь» у админа по ключу — живая сессия. */
  token: PanelAuth;
  user: AuthUser | null;
  onBackToAdmin?: () => void;
}

function UserPanel({ token, user, onBackToAdmin }: UserPanelProps) {
  const isAdminPreview = !!onBackToAdmin;
  // Авто-телеметрия всей панели (заходы/вкладки/клики) → визор-лог scope 'panel'.
  // Включена ВСЕГДА, пока смонтирована UserPanel: хук живёт только здесь (в AdminPanel
  // его нет), а UserPanel всегда работает под JWT САМОГО смотрящего — в т.ч. admin-preview
  // (админ жмёт «user view» → видит СВОЮ панель под своим же tg_id), поэтому атрибуция
  // всегда корректна, чужой лог не загрязняется. Раньше !isAdminPreview глушил ровно этот
  // кейс (админ-ректор шарится в своей панели через preview) → его броузинг не писался.
  // apiBase — модульная const; token — из JWT-сессии (user_id сервер берёт из него, П0).
  usePanelTelemetry({ apiBase, token, enabled: true });
  const headers = useMemo(() => authHeaders(token), [token]);
  // Check all possible user ID fields (Telegram auth: user.id = tg_id, Web auth: user.tgId)
  // userTgId kept for future per-user context features — intentionally unused right now.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _userTgId = String(user?.tg_id || user?.tgId || user?.id || '');

  // Контекстный чат: приоритет start_param > initDataUnsafe.chat (attachment menu, редко)
  const contextChatId = useMemo<number | null>(() => {
    // @ts-expect-error TODO migration: Telegram WebApp types
    const tg = window?.Telegram?.WebApp;
    const sp: string = tg?.initDataUnsafe?.start_param || '';
    // persona_-4521172039 → chatId = -4521172039
    if (sp.startsWith('persona_')) {
      const parsed = parseInt(sp.replace('persona_', ''));
      if (!isNaN(parsed)) return parsed;
    }
    // Attachment menu fallback (доступно только крупным рекламодателям)
    return tg?.initDataUnsafe?.chat?.id || null;
  }, []);

  // Feature flags из БД (вместо хардкода). Admin preview → всё включено
  const [showPersona, setShowPersona] = useState<boolean>(isAdminPreview);
  const [showStyle, setShowStyle] = useState<boolean>(false);

  useEffect(() => {
    if (isAdminPreview) {
      setShowPersona(true); /* showStyle НЕ ставим — только через API */
    }
    const url = contextChatId
      ? `${apiBase}/v1/my-features?chatId=${contextChatId}`
      : `${apiBase}/v1/my-features`;
    fetch(url, { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.features?.persona_enabled) setShowPersona(true);
        if (data?.features?.personality_switcher) setShowStyle(true);
      })
      .catch(() => {
        // Intentional: feature flags default to conservative (off) on failure.
      });
  }, [headers, isAdminPreview, contextChatId]);

  // ── Theme: dark / light / system ──
  const [themeMode, setThemeMode] = useState<string>(
    () => localStorage.getItem('padla_theme') || 'system'
  );
  const [tick, setTick] = useState<number>(0); // force re-resolve on external changes

  function resolveTheme(mode: string): 'light' | 'dark' {
    if (mode === 'dark' || mode === 'light') return mode;
    // @ts-expect-error TODO migration: Telegram WebApp types
    const tgScheme = window?.Telegram?.WebApp?.colorScheme;
    if (tgScheme === 'light' || tgScheme === 'dark') return tgScheme;
    if (window.matchMedia?.('(prefers-color-scheme: light)').matches) return 'light';
    return 'dark';
  }

  const resolvedTheme = useMemo(() => resolveTheme(themeMode), [themeMode, tick]);

  // Тема — в DOM ДО отрисовки (useLayoutEffect): после отрисовки первый кадр панели рисовался
  // тёмным фоном общей таблицы стилей и лишь потом светлел — у людей со светлой темой Telegram
  // «моргало» (26 сен 2026).
  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-padla-theme', resolvedTheme);
    document.body.style.background = resolvedTheme === 'light' ? '#FAFAF8' : '#0A0A0A';
    document.body.style.color = resolvedTheme === 'light' ? '#1A1816' : '#E8E4E0';
    return () => {
      document.documentElement.removeAttribute('data-padla-theme');
      document.body.style.background = '';
      document.body.style.color = '';
    };
  }, [resolvedTheme]);

  // Listen for Telegram theme changes + OS preference changes
  useEffect(() => {
    const bump = (): void => setTick((t) => t + 1);
    // @ts-expect-error TODO migration: Telegram WebApp types
    window?.Telegram?.WebApp?.onEvent?.('themeChanged', bump);
    const mq = window.matchMedia?.('(prefers-color-scheme: light)');
    mq?.addEventListener?.('change', bump);
    return () => {
      // @ts-expect-error TODO migration: Telegram WebApp types
      window?.Telegram?.WebApp?.offEvent?.('themeChanged', bump);
      mq?.removeEventListener?.('change', bump);
    };
  }, []);

  const cycleTheme = useCallback(() => {
    const next = themeMode === 'system' ? 'light' : themeMode === 'light' ? 'dark' : 'system';
    localStorage.setItem('padla_theme', next);
    setThemeMode(next);
  }, [themeMode]);

  const [showTutorial, setShowTutorial] = useState<boolean>(false);
  // Стартовый грант и срок для обучающего экрана — из кассы (SSOT базы), не литерал.
  const pricingForTutorial = usePricing(token, { enabled: !!token && showTutorial });

  // Показ туториала: сервер — истина (оба края панели, panel./ru.panel., видят одно), localStorage —
  // быстрый кэш/фолбэк. Начальное false → нет вспышки модалки существующему юзеру до ответа сети.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const serverCompleted = await fetchTutorialCompleted(apiBase, token);
      if (cancelled) return;
      const localCompleted = localStorage.getItem('padla_tutorial_completed') === 'true';
      const { show, migrate } = resolveTutorialVisibility({ serverCompleted, localCompleted });
      if (migrate) postTutorialComplete(apiBase, token); // localStorage знает, сервер ещё нет → мигрируем
      setShowTutorial(show);
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleCompleteTutorial = (): void => {
    localStorage.setItem('padla_tutorial_completed', 'true');
    postTutorialComplete(apiBase, token);
    setShowTutorial(false);
    // Open bot chat
    handleGoToBot();
  };

  const handleCloseTutorial = (): void => {
    localStorage.setItem('padla_tutorial_completed', 'true');
    postTutorialComplete(apiBase, token);
    setShowTutorial(false);
  };

  const handleGoToBot = (): void => {
    const botUrl = config.botLink;

    // @ts-expect-error TODO migration: Telegram WebApp types
    if (window.Telegram?.WebApp?.initData) {
      // @ts-expect-error TODO migration: Telegram WebApp types
      window.Telegram.WebApp.openTelegramLink(botUrl);
      setTimeout(() => {
        // @ts-expect-error TODO migration: Telegram WebApp types
        window.Telegram.WebApp.close();
      }, 100);
    } else {
      window.open(botUrl, '_blank');
    }
  };

  // Tab config (ревизия ректора 17 июл): только Баланс / Стиль (по флагу) / Рефералы.
  // «Группы» скрыты (делались под одного пользователя, никто не пользуется) и «Персона»
  // скрыта для всех (устарела) — РОУТЫ живы (deep-links persona_* работают), убраны
  // только вкладки. Входы в студии — плитки-двери на главной (UserBalance), не вкладки.
  const tabs: Array<{ to: string; label: string; end?: boolean }> = [
    { to: '/', label: 'Баланс', end: true },
    ...(showStyle ? [{ to: '/style', label: 'Стиль' }] : []),
    { to: '/referrals', label: 'Рефералы' },
  ];

  const themeIcon =
    themeMode === 'light' ? (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      >
        <circle cx="12" cy="12" r="5" />
        <line x1="12" y1="1" x2="12" y2="3" />
        <line x1="12" y1="21" x2="12" y2="23" />
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
        <line x1="1" y1="12" x2="3" y2="12" />
        <line x1="21" y1="12" x2="23" y2="12" />
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
      </svg>
    ) : themeMode === 'dark' ? (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      >
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
      </svg>
    ) : (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      >
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    );

  return (
    <PanelAuthContext.Provider value={token}>
      <div
        className="min-h-screen flex flex-col"
        style={{
          background: 'var(--padla-bg)',
          fontFamily: 'var(--font-body)',
          color: 'var(--padla-text)',
        }}
      >
        {showTutorial && (
          <Tutorial
            onComplete={handleCompleteTutorial}
            onClose={handleCloseTutorial}
            welcomeGrant={pricingForTutorial.data?.welcome_grant ?? null}
            welcomeTtlDays={pricingForTutorial.data?.welcome_grant_ttl_days ?? null}
          />
        )}

        {isAdminPreview && (
          <div className="max-w-md mx-auto w-full px-5 pt-3">
            <button
              onClick={onBackToAdmin}
              className="w-full py-2 text-xs transition-colors"
              style={{
                borderRadius: 10,
                background: 'var(--padla-accent-bg)',
                border: '1px solid var(--padla-border)',
                color: 'var(--padla-accent)',
              }}
            >
              Admin Panel
            </button>
          </div>
        )}

        {/* Header */}
        <div className="max-w-md mx-auto w-full px-5 pt-6 pb-1">
          {/* Top row: brand + theme toggle */}
          <div className="flex items-center justify-between mb-5 padla-fade-up">
            <div
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 26,
                fontWeight: 400,
                letterSpacing: '0.15em',
                color: 'var(--padla-accent)',
              }}
            >
              PADLA
            </div>
            <button
              onClick={cycleTheme}
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--padla-bg-card)',
                border: '1px solid var(--padla-border)',
                color: 'var(--padla-text-2)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {themeIcon}
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-3 padla-fade-up" style={{ animationDelay: '0.05s' }}>
            {tabs.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.end}
                className="flex-1 text-center transition-all duration-200"
                style={({ isActive }) => ({
                  padding: '9px 0',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: isActive ? 500 : 400,
                  background: isActive ? 'var(--padla-accent-bg)' : 'transparent',
                  color: isActive ? 'var(--padla-text)' : 'var(--padla-text-2)',
                })}
              >
                {tab.label}
              </NavLink>
            ))}
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 padla-fade-up" style={{ animationDelay: '0.1s' }}>
            <button
              onClick={handleGoToBot}
              className="flex-1 flex items-center justify-center gap-2 transition-all duration-200"
              style={{
                padding: '10px 0',
                borderRadius: 10,
                background: 'var(--padla-bg-card)',
                border: '1px solid var(--padla-border)',
                color: 'var(--padla-text-2)',
                fontSize: 12,
              }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Открыть чат
            </button>
            <button
              onClick={() => setShowTutorial(true)}
              className="flex-1 flex items-center justify-center gap-2 transition-all duration-200"
              style={{
                padding: '10px 0',
                borderRadius: 10,
                background: 'var(--padla-bg-card)',
                border: '1px solid var(--padla-border)',
                color: 'var(--padla-text-2)',
                fontSize: 12,
              }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              Инструкция
            </button>
          </div>
        </div>

        {/* Page content */}
        <div style={{ flex: 1 }}>
          <Suspense
            fallback={
              <div className="px-5 pt-6 flex justify-center">
                <Spinner />
              </div>
            }
          >
            <Routes>
              <Route
                index
                element={
                  <div className="px-5 pt-2 pb-8">
                    <UserBalance apiBase={apiBase} user={user} />
                  </div>
                }
              />
              {showStyle && (
                <Route
                  path="/style"
                  element={
                    <div className="px-5 pt-2 pb-8">
                      <StyleSwitcher apiBase={apiBase} user={user} />
                    </div>
                  }
                />
              )}
              {showPersona && (
                <Route
                  path="/persona"
                  element={<UserPersona headers={headers} apiBase={apiBase} user={user} />}
                />
              )}
              <Route
                path="/referrals"
                element={<Referrals headers={headers} apiBase={apiBase} user={user} />}
              />
              <Route
                path="/groups"
                element={
                  <div className="px-5 pt-2 pb-8">
                    <MyGroups headers={headers} apiBase={apiBase} user={user} />
                  </div>
                }
              />
              <Route
                path="/groups/:chatId"
                element={
                  <div className="px-5 pt-2 pb-8">
                    <MyGroupDetail headers={headers} apiBase={apiBase} user={user} />
                  </div>
                }
              />
              <Route
                path="/gift/:token"
                element={<GiftRedeem headers={headers} apiBase={apiBase} />}
              />
              {/* Legacy digest deep-link → redirect to balance */}
              <Route path="/digest" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </div>
      </div>
    </PanelAuthContext.Provider>
  );
}

// Компонент который проверяет нужна ли авторизация для текущего роута
function AppContent(): React.ReactElement | null {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const deepLinkHandled = useRef<boolean>(false);
  const [previewAsUser, setPreviewAsUser] = useState<boolean>(false);

  const handleBackToAdmin = useCallback(() => {
    setPreviewAsUser(false);
    navigate('/', { replace: true });
  }, [navigate]);

  // Публичные роуты (без авторизации) — например, после оплаты браузер открывает эту страницу
  if (location.pathname === '/payment-return' || location.pathname.startsWith('/payment-return')) {
    return <PaymentReturn />;
  }

  // Подарок кредитов по старой ссылке …/gift/<token> ВНЕ Telegram (браузер): панель тут не
  // знает человека — раньше это был вход администратора, и подарок было не получить.
  // Подарок получают в чате с Падлой (26 сен 2026); экран ведёт туда одним нажатием.
  // Внутри мини-аппа (есть initData) — прежняя страница GiftRedeem ниже.
  if (location.pathname.startsWith('/gift/') && !isTelegramWebApp()) {
    return <GiftToChat token={location.pathname.slice('/gift/'.length).split('/')[0] ?? ''} />;
  }

  // Привязка первого ключа админки по одноразовой ссылке от бота (/admin в личку). Публичная:
  // вход ещё не выполнен; токен — во фрагменте адреса, страница читает и стирает его сама.
  if (location.pathname === '/admin/enroll') {
    return <AdminEnroll apiBase={apiBase} />;
  }

  // Стенд ВСЕХ data-driven экранов Студии (dev-tool, мок-сеть + SSE, без backend/
  // трат и без авторизации — как Login). ВЫШЕ auth-гейта: скриншот-цикл заходит
  // прямым URL без токена. Гоняет настоящие экраны AstroShell (П9). См. ScreensStand.tsx.
  if (location.pathname.startsWith('/studios-screens-stand')) {
    return <ScreensStand />;
  }

  // Знакомства с Падлой нет — кабинет не открывается: сначала переписка (08.08.2026).
  if (auth.meet) {
    return <MeetPadlaWall botUrl={auth.meet.botUrl} variant={auth.meet.variant} />;
  }

  // Для остальных роутов нужна авторизация
  // ВАЖНО: авторизация должна завершиться ДО deep link редиректа!
  // Тот же экран загрузки, что и у скачивания экранов (BootSplash): одна заставка на весь путь
  // «открыл → увидел», без смены вида посередине.
  if (auth.loading) {
    return <BootSplash />;
  }

  const panelAuth = panelAuthOf(auth);
  // Админка — только у живой сессии по ключу доступа. Вход из Telegram — пользователь всегда
  // (сервер и так выдаёт ему роль «пользователь»; здесь — чтобы экран не зависел от надписи).
  const isAdmin = auth.authMethod === 'passkey' && ['admin', 'superadmin'].includes(auth.role);

  // Не вошли и не в Telegram — публичная страница: «Падла живёт в Telegram» и неприметное
  // «Войти» (ключ доступа). Ни слова об админке, командах и старом входе по паролю — страницу
  // видит любой (решение ректора 26 сен).
  if (!panelAuth && !isTelegramWebApp()) {
    return (
      <AdminLogin
        apiBase={apiBase}
        onLogin={auth.handlePasskeyLogin}
        notice={auth.notice ?? null}
      />
    );
  }

  // Ошибка авторизации (только для Telegram, т.к. web показывает Login)
  if (auth.error && isTelegramWebApp()) {
    return <div className="p-4 text-red-400">Ошибка: {auth.error}</div>;
  }

  // Deep link редирект: start_param=digest_* → /digest (→ redirect to /)
  // @ts-expect-error TODO migration: Telegram WebApp types
  const startParam: string = window?.Telegram?.WebApp?.initDataUnsafe?.start_param || '';
  if (startParam.startsWith('digest_') && location.pathname === '/' && !deepLinkHandled.current) {
    deepLinkHandled.current = true;
    return <Navigate to="/digest" replace />;
  }

  // Deep link: startapp=persona или persona_CHATID → /persona
  if (
    (startParam === 'persona' || startParam.startsWith('persona_')) &&
    location.pathname === '/' &&
    !deepLinkHandled.current
  ) {
    deepLinkHandled.current = true;
    return <Navigate to="/persona" replace />;
  }

  // Deep link: startapp=referrals или referrals_CHATID → /referrals. Кнопка
  // «Пусть платят другие» из биллинг-CTA (низкий баланс). chatId в суффиксе для
  // консистентности формы; роутинг по нему не зависит — панель резолвит юзера из
  // initData. Парная сторона: buildBillingCtaKeyboard (@padla/core/billing/billing-cta).
  if (
    (startParam === 'referrals' || startParam.startsWith('referrals_')) &&
    location.pathname === '/' &&
    !deepLinkHandled.current
  ) {
    deepLinkHandled.current = true;
    return <Navigate to="/referrals" replace />;
  }

  // Deep link: startapp=astrocompat_<token> — намерение «совместимость с человеком из чата»
  // (offer_compatibility). Токен едет в студию query ?compat=; AstroShell читает intent
  // (имя) и приземляет РОВНО на действие (разбор синастрии ИЛИ ввод данных того человека),
  // НЕ «студия вообще». chatId НЕ передаём: студия резолвит по своей партиции (initData).
  // Ставим ВЫШЕ ветки astro_ — порядок защищает (хотя 'astrocompat_' и не матчит 'astro_').
  if (
    startParam.startsWith('astrocompat_') &&
    location.pathname === '/' &&
    !deepLinkHandled.current
  ) {
    deepLinkHandled.current = true;
    const token = startParam.slice('astrocompat_'.length);
    return <Navigate to={`/astro?compat=${encodeURIComponent(token)}`} replace />;
  }

  // Deep link: startapp=pairview_<partnerId> — «Посмотреть вас двоих» из чата
  // (Искра/согласие, река знакомств 5 авг): открыть студию СРАЗУ на экране
  // сравнения собранной пары, а не на «Сегодня». Ставим выше astro_ (не матчит).
  if (startParam.startsWith('pairview_') && location.pathname === '/' && !deepLinkHandled.current) {
    deepLinkHandled.current = true;
    const partner = startParam.slice('pairview_'.length);
    return <Navigate to={`/astro?pair=${encodeURIComponent(partner)}`} replace />;
  }

  // Deep link: startapp=astronew — вход живой пробы AstroShell (ссылка уже отправлена
  // ректору). После подмены входа эквивалентен обычному входу; оставлен, чтобы
  // отправленная ссылка жила. 'astronew' НЕ матчит 'astro_' (нет «_»).
  if (startParam === 'astronew' && location.pathname === '/' && !deepLinkHandled.current) {
    deepLinkHandled.current = true;
    return <Navigate to="/astro" replace />;
  }

  // Deep link: startapp=astro_CHATID → астрослужба. web_app-кнопки запрещены Telegram
  // в ГРУППАХ → бот шлёт t.me?startapp=, тут роутим в студию с этим чатом. chat едет
  // в URL → resolveChatId его читает. numero_ — легаси несуществующей нумерологии
  // (бот такие не шлёт с июля; долетевший старый — мягко в /astro, П5).
  if (
    (startParam.startsWith('astro_') || startParam.startsWith('numero_')) &&
    location.pathname === '/' &&
    !deepLinkHandled.current
  ) {
    deepLinkHandled.current = true;
    const isAstro = startParam.startsWith('astro_');
    const chatId = startParam.slice((isAstro ? 'astro_' : 'numero_').length);
    return <Navigate to={`/astro?chat=${encodeURIComponent(chatId)}`} replace />;
  }

  // Deep link: startapp=<kind>_<id> — холодный гость по виральной ссылке (Срез 3/4).
  // Виды крючков — ЕДИНЫЙ реестр @padla/core/studios/share-kinds (П9: раньше префиксы
  // дублировались литералами, и новый вид на сервере без правки здесь давал тихо мёртвую
  // ссылку — гость приземлялся на «/»). Токен приглашения едет в студию query ?invite=;
  // AstroShell гасит его (redeem) → допуск + экран-ревил → онбординг. chatId НЕ передаём:
  // у гостя своя личная партиция (resolveChatId упадёт на его tg-id). Все вектора ведут в
  // астро-студию.
  if (
    isStudioShareStartParam(startParam) &&
    location.pathname === '/' &&
    !deepLinkHandled.current
  ) {
    deepLinkHandled.current = true;
    return <Navigate to={`/astro?invite=${encodeURIComponent(startParam)}`} replace />;
  }

  // Deep link: startapp=tracks_<chatId> — Радио Падла (редактор песен). Той же формы URL-кнопкой
  // шлёт бот (surface_service / реестр служб), т.к. web_app-кнопки запрещены в группах. Track Studio
  // резолвит юзера по initData (своя identity), chatId в URL не нужен — поэтому просто редирект.
  if (startParam.startsWith('tracks_') && location.pathname === '/' && !deepLinkHandled.current) {
    deepLinkHandled.current = true;
    return <Navigate to="/track-studio/" replace />;
  }

  // Deep link: startapp=track_<trackId> — редактор КОНКРЕТНОЙ песни. Кнопка «Редактировать»
  // на пульте заказа: в личке она web_app, а в группе Telegram web_app запрещает — там та же
  // кнопка приходит этой ссылкой (билдер buildTrackEditorUrl, реестр служб). До 3 авг 2026
  // в группах кнопки редактора не было вовсе.
  if (startParam.startsWith('track_') && location.pathname === '/' && !deepLinkHandled.current) {
    deepLinkHandled.current = true;
    const trackId = startParam.slice('track_'.length);
    return <Navigate to={trackId ? `/track-studio/track/${trackId}` : '/track-studio/'} replace />;
  }

  // Track Studio — standalone full-screen mini-app, без UserPanel/AdminPanel chrome.
  // Открывается ТОЛЬКО через web_app кнопку из Telegram-чата при редактировании
  // конкретного трека (Stage 2 / Stage 5 inline-кнопка «🎨 Открыть редактор»).
  // После закрытия mini-app из Telegram — Track Studio исчезает, **в UserPanel
  // ссылок на него нет** (UserPanel итак перенасыщена, отдельная вкладка не
  // добавляется). См. docs/superpowers/specs/2026-05-16-suno-track-studio-miniapp-verification-report.md
  if (location.pathname.startsWith('/track-studio')) {
    return <TrackStudioShell />;
  }

  // Tests Studio — standalone full-screen mini-app (по образу Track Studio).
  // Открывается через web_app inline button из bot чата (см. services/telegram-gateway/
  // modules/tests-studio/module.ts). В UserPanel ссылок нет — эфемерен относительно
  // основного personal cabinet'а. Routing per services/panel/src/ui/pages/TestsStudio/README.md.
  if (location.pathname.startsWith('/tests-studio')) {
    return <TestsStudioShell />;
  }

  // Astrology / Numerology Studios — standalone full-screen Mini App (SP-1 граф людей).
  // Дизайн: docs/superpowers/specs/2026-05-26-astrology-numerology-graph-of-people-design.md
  // Открывается через web_app inline button из bot чата. Общий shell в StudiosShared/.
  // Стенд анимации колеса/loader'а (dev-tool, без backend/трат). Прямой URL.
  if (location.pathname.startsWith('/studios-anim')) {
    return <AnimTestHarness />;
  }

  // Prefill Preview — creator/admin-only стенд first-touch на РЕАЛЬНЫХ данных:
  // искры + кино-вход для любого ?chat=M&user=N. В проде этот экран виден один раз
  // (онбординг нового юзера), стенд позволяет смотреть повторно для валидации судьи.
  // Настоящий гейт — на сервере (prefill-preview, admin/superadmin). Здесь дублируем
  // по auth.role (defense-in-depth, не security-гарантия).
  if (location.pathname.startsWith('/studios-prefill')) {
    if (!isAdmin) {
      return <div className="p-4 text-zinc-400">Страница не найдена.</div>;
    }
    return <PrefillPreviewStand token={panelAuth as PanelAuth} />;
  }

  // Studios Sandbox — ЕДИНЫЙ вьюер реального пути: донор → искры → данные → колесо
  // (read-only, инкремент 1). Одна ссылка вместо россыпи стендов. Гейт admin (сервер).
  if (location.pathname.startsWith('/studios-sandbox')) {
    if (!isAdmin) {
      return <div className="p-4 text-zinc-400">Страница не найдена.</div>;
    }
    return <StudiosSandboxViewer token={panelAuth as PanelAuth} />;
  }

  // ПОДМЕНА ВХОДА (приказ ректора 6 авг): /astrology-studio → /astro. Уже отправленные
  // web_app-кнопки и ссылки в чатах несут старый адрес — редирект держит их живыми
  // НАВСЕГДА (маппер путей + query как есть; тесты в astro-routes.test.ts).
  if (location.pathname.startsWith('/astrology-studio')) {
    return <Navigate to={legacyAstrologyPathToAstro(location.pathname, location.search)} replace />;
  }
  // Нумерологии не существует (ректор, 6 авг; бот входов не шлёт, визитов ноль) —
  // её шелл СНЕСЁН (П22). Долетевшая старая ссылка мягко ведёт в астрослужбу (П5).
  if (location.pathname.startsWith('/numerology-studio')) {
    return <Navigate to="/astro" replace />;
  }

  // Витрина астрослужбы (AstroShell). Точный предикат — /astrology-studio ловится выше.
  if (location.pathname === '/astro' || location.pathname.startsWith('/astro/')) {
    return <AstroShell />;
  }

  // Gift redeem page always uses UserPanel (even for admins)
  const isGiftRoute = location.pathname.startsWith('/gift/');

  return (
    <div className="min-h-full">
      {isAdmin && !isGiftRoute ? (
        previewAsUser ? (
          <UserPanel
            token={panelAuth as PanelAuth}
            user={auth.user}
            onBackToAdmin={handleBackToAdmin}
          />
        ) : (
          <AdminPanel
            auth={panelAuth as PanelAuth}
            user={auth.user}
            authMethod={auth.authMethod}
            onLogout={auth.handleLogout}
            onPreviewAsUser={() => {
              setPreviewAsUser(true);
              navigate('/', { replace: true });
            }}
          />
        )
      ) : (
        <UserPanel token={panelAuth as PanelAuth} user={auth.user} />
      )}
    </div>
  );
}

// Fallback UI shown when Sentry.ErrorBoundary catches an unhandled error
// above our React tree. Our custom ErrorBoundary inside renders the rich
// fallback for in-tree errors.
function SentryFallback(): React.ReactElement {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-zinc-900 rounded-xl border border-zinc-800 p-6 text-center">
        {/* Тексты — те же, что у внутренней крашкарты (ErrorBoundary.tsx, русифицирована
            2bed465c3): последний англоязычный экран панели, который видели люди
            (крашкарта старой оболочки 1 сен 2026). Кнопка одна: этот экран значит,
            что упало ВСЁ приложение — «попробовать снова» нечем, только перезагрузка. */}
        <h1 className="text-xl font-bold text-red-400 mb-2">Что-то сломалось</h1>
        <p className="text-zinc-400 mb-4">
          Уже записал, что случилось. Перезагрузи страницу — обычно помогает.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-medium transition-colors"
        >
          Перезагрузить
        </button>
      </div>
    </div>
  );
}

// ОДНА заставка на весь путь «открыл → увидел» (26 сен 2026): пока проверяется вход и пока
// качается кусок экрана. Цвет — цвет окна Telegram (`--boot-bg` ставит встроенный скрипт
// index.html из themeParams; вне Telegram — тёмный), тот же, что у первого кадра страницы:
// между кадрами ничего не меняется. До этого загрузка проходила три разных вида подряд — белый
// кадр, «Загрузка…» и фиолетовую заставку с луной Астростудии на любом экране, и ректор видел,
// как «моргнули» панель и студия, прежде чем открылась страница. Без эмодзи и надписей: экран
// назначения ещё неизвестен, заставка не выдаёт себя ни за один из них. Стили — инлайн: до
// любого CSS-чанка (studios.css и прочие грузятся позже): стили и первая копия разметки — в
// index.html.
function BootSplash(): React.ReactElement {
  // Та же разметка, что лежит в #root index.html до загрузки скриптов; стили — там же, встроенные
  // (до любого CSS-чанка): смена статической заставки на эту не видна глазу.
  return (
    <div className="padla-boot" role="status" aria-label="Загрузка">
      <span className="padla-boot-dot" />
    </div>
  );
}

export default function App(): React.ReactElement {
  return (
    <Sentry.ErrorBoundary fallback={<SentryFallback />}>
      <ErrorBoundary>
        <BrowserRouter>
          {/* Один внешний Suspense ловит подгрузку lazy-экранов из ранних return'ов
              AppContent (студии, Login, payment-return, стенды). Вложенные Suspense
              в AdminPanel/UserPanel ниже ловят переключение их роутов, сохраняя
              хром (навбар не мигает). */}
          <Suspense fallback={<BootSplash />}>
            <AppContent />
          </Suspense>
        </BrowserRouter>
      </ErrorBoundary>
    </Sentry.ErrorBoundary>
  );
}
